import { useCallback, useEffect, useState } from "react";
import { Alert, App, Button, Empty, Modal, Space, Table, Tag, Upload } from "antd";
import type { OutboundImportRow } from "@shoe-trace/shared";
import { commitOutbound, fetchPickList, listOrders, previewImport, type ImportIssue, type OutboundOrder, type PickLine } from "../api/client";
import { LoadFail, PageSpin } from "../components/widgets";
import { canManage, useSession } from "../session";

const ORDER_STATUS: Record<string, string> = {
  OPEN: "待拣货",
  IN_PROGRESS: "复核中",
  SHIPPED: "已出库",
};

const OUTBOUND_TEMPLATE = "SKU,双数,渠道,目的仓\nST1920white34,4,亚马逊 FBA,ONT8\n";

export function Picking(): JSX.Element {
  const { message } = App.useApp();
  const { user } = useSession();
  const manager = canManage(user);
  const [orders, setOrders] = useState<OutboundOrder[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [lines, setLines] = useState<PickLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [previewRows, setPreviewRows] = useState<OutboundImportRow[]>([]);
  const [issues, setIssues] = useState<ImportIssue[]>([]);
  const [total, setTotal] = useState(0);

  const loadOrders = useCallback(() => {
    setLoading(true);
    void listOrders()
      .then((rows) => {
        setOrders(rows);
        setFailed(false);
        setSelected((current) => current ?? rows[0]?.orderNo ?? null);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (!selected) return;
    void fetchPickList(selected)
      .then((result) => setLines(result.lines))
      .catch(() => setFailed(true));
  }, [selected]);

  if (loading && orders.length === 0) return <PageSpin tip="正在按先入库先出库排列库位…" />;
  if (failed && orders.length === 0) return <LoadFail title="拣货清单没有生成。" onRetry={loadOrders} />;

  return (
    <>
      <Alert type="info" showIcon message="按表从最早入库的批次拣。拣的时候不用逐双扫码。拿到复核台再一双双扫，扫过再放进箱子。" />
      {manager ? (
        <Button
          className="no-print"
          type="primary"
          style={{ margin: "16px 0" }}
          onClick={() => {
            setPreviewRows([]);
            setIssues([]);
            setTotal(0);
            setOpen(true);
          }}
        >
          导入出库清单并生成拣货单
        </Button>
      ) : null}
      {orders.length === 0 ? (
        <Empty description="还没有出库单。请导入清单，每行写 SKU、双数、渠道和目的仓。" />
      ) : (
        <>
          <Table
            className="no-print"
            rowKey="orderNo"
            dataSource={orders}
            pagination={{ pageSize: 20 }}
            onRow={(row) => ({ onClick: () => setSelected(row.orderNo) })}
            columns={[
              { title: "出库单号", dataIndex: "orderNo" },
              { title: "渠道", dataIndex: "channel" },
              { title: "目的仓", dataIndex: "targetWarehouse" },
              { title: "SKU 行数", dataIndex: "lineCount" },
              { title: "需求双数", dataIndex: "totalQty" },
              { title: "已复核双数", dataIndex: "verifiedQty" },
              { title: "状态", dataIndex: "status", render: (value: string) => <Tag>{ORDER_STATUS[value] ?? value}</Tag> },
              { title: "快递单号", dataIndex: "trackingNo", render: (value: string | null) => value ?? "—" },
            ]}
          />
          <Space className="no-print" style={{ margin: "12px 0" }}>
            <strong>当前拣货单 {selected}</strong>
            <Button onClick={() => window.print()}>打印这张拣货单</Button>
          </Space>
          <Table
            className="print-area"
            rowKey={(row) => `${row.seq}-${row.humanCode}-${row.batchNo}`}
            pagination={false}
            dataSource={lines}
            columns={[
              { title: "先拣顺序", dataIndex: "seq" },
              { title: "库位", dataIndex: "locationCode", render: (value: string) => <span style={{ fontSize: 20, fontWeight: 700 }}>{value || "—"}</span> },
              { title: "批次", dataIndex: "batchNo", render: (value: string) => value || "—" },
              { title: "入库日", dataIndex: "inboundDate", render: (value: string) => (value ? value.slice(0, 10) : "—") },
              { title: "SKU", dataIndex: "humanCode" },
              {
                title: "应拣双数",
                dataIndex: "qty",
                render: (value: number, row: PickLine) => (value === 0 && row.note ? row.note : value),
              },
              { title: "库龄（天）", dataIndex: "ageDays" },
            ]}
          />
          <p style={{ color: "#8c8c8c" }}>
            只从可用的鞋里，按入库早晚分配库位和双数。可用是已经上架、还没装进出库单的双。这张表只是指引，拣的时候不改变每双的状态。
          </p>
        </>
      )}
      <Modal
        destroyOnClose
        width={720}
        open={open}
        title="导入出库清单并生成拣货单"
        okText="写入这张出库单"
        cancelText="先不导入"
        okButtonProps={{ disabled: previewRows.length === 0 || issues.length > 0 }}
        onCancel={() => setOpen(false)}
        onOk={() => {
          void commitOutbound(previewRows)
            .then((created) => {
              message.success(`出库单 ${created.orderNo} 已生成，共 ${created.lineCount} 个 SKU。把拣货单打出来，按第 1 行的库位先拣。`);
              setOpen(false);
              setSelected(created.orderNo);
              loadOrders();
            })
            .catch((error: unknown) => message.error(error instanceof Error ? error.message : "拣货清单没有生成。"));
        }}
      >
        <Upload
          accept=".csv,.xlsx"
          maxCount={1}
          beforeUpload={(file) => {
            void previewImport("outbound", file)
              .then((preview) => {
                setPreviewRows(preview.rows as OutboundImportRow[]);
                setIssues(preview.issues);
                setTotal(preview.total);
              })
              .catch((error: unknown) => message.error(error instanceof Error ? error.message : "这份清单没有读出来。"));
            return false;
          }}
        >
          <Button>选择出库清单</Button>
        </Upload>
        <Button
          style={{ margin: "12px 0" }}
          onClick={() => {
            const blob = new Blob([OUTBOUND_TEMPLATE], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "出库单导入模板.csv";
            link.click();
            URL.revokeObjectURL(url);
          }}
        >
          下载出库单导入模板
        </Button>
        <p>共读到 {total} 行</p>
        {issues.map((issue) => (
          <p key={`${issue.row}-${issue.message}`} style={{ color: "#ff4d4f" }}>
            {issue.message}
          </p>
        ))}
        <Table
          rowKey={(_row, index) => String(index)}
          dataSource={previewRows}
          pagination={{ pageSize: 50 }}
          columns={[
            { title: "SKU", dataIndex: "humanCode" },
            { title: "双数", dataIndex: "requiredQty" },
            { title: "渠道", dataIndex: "channel" },
            { title: "目的仓", dataIndex: "targetWarehouse" },
          ]}
        />
      </Modal>
    </>
  );
}
